<template>
  <div class="settings-page-wrapper">
    <!-- 装饰背景微光 -->
    <div class="ambient-glow glow-1"></div>
    <div class="ambient-glow glow-2"></div>

    <div class="settings-card-container">
      <!-- 顶部 Header 卡片 -->
      <div class="settings-header-card">
        <div class="header-left">
          <div class="header-icon-box">
            <el-icon><UserFilled /></el-icon>
          </div>
          <div class="header-text">
            <h2 class="title">
              {{ $t("settings.title") || "创作者个人与安全设置" }}
            </h2>
            <p class="sub-title">
              管理您的创作者公开档案、头像信息与账号安全凭据
            </p>
          </div>
        </div>

        <div class="header-right">
          <span class="account-type-badge">
            <el-icon><Key /></el-icon>
            {{ form.identity || "创作者" }}
          </span>
        </div>
      </div>

      <!-- 表单主体卡片 -->
      <div class="settings-form-panel">
        <el-form
          :model="form"
          :rules="rules"
          ref="formRef"
          label-position="top"
          class="modern-settings-form"
        >
          <!-- 区域一：基础个人资料 -->
          <div class="form-section-block">
            <div class="section-title-row">
              <span class="indicator-bar"></span>
              <span class="section-name">基本资料与公开档案</span>
            </div>

            <div class="profile-avatar-row">
              <!-- 头像上传区 -->
              <div class="avatar-uploader-box">
                <el-upload
                  class="avatar-uploader"
                  :action="UpdateApi"
                  :show-file-list="false"
                  :on-success="handleAvatarSuccess"
                  :before-upload="beforeAvatarUpload"
                  :limit="1"
                  :file-list="fileList"
                  :disabled="allowUpload"
                >
                  <div v-if="imageUrl" class="uploaded-avatar-wrapper">
                    <el-image :src="imageUrl" class="avatar-img" fit="cover">
                      <template #error>
                        <div class="avatar-fallback">
                          <el-icon><PictureFilled /></el-icon>
                        </div>
                      </template>
                    </el-image>
                    <div class="avatar-hover-mask">
                      <span
                        class="del-icon-btn"
                        @click.stop="clearImage"
                        title="清除并重新上传"
                      >
                        <el-icon><Delete /></el-icon>
                      </span>
                    </div>
                  </div>
                  <div v-else class="avatar-placeholder">
                    <el-icon class="upload-icon"><Plus /></el-icon>
                    <span class="upload-txt">更换头像</span>
                  </div>
                </el-upload>
                <span class="avatar-tip">建议尺寸 500×500px，不超过 2MB</span>
              </div>

              <!-- 基础信息字段 -->
              <div class="profile-fields">
                <el-form-item
                  :label="$t('settings.username') || '创作者名称'"
                  prop="name"
                >
                  <el-input
                    v-model="form.name"
                    :placeholder="
                      $t('settings.usernamePlaceholder') || '请输入创作者昵称'
                    "
                    clearable
                    size="large"
                    :prefix-icon="User"
                  />
                </el-form-item>

                <el-form-item label="创作者身份级别" prop="identity">
                  <el-select
                    v-model="form.identity"
                    style="width: 100%"
                    placeholder="请选择"
                    disabled
                    size="large"
                  >
                    <el-option
                      v-for="item in options"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </el-form-item>
              </div>
            </div>
          </div>

          <!-- 区域二：绑定邮箱 -->
          <div class="form-section-block">
            <div class="section-title-row">
              <span class="indicator-bar"></span>
              <span class="section-name">安全通讯绑定</span>
            </div>

            <el-form-item label="安全邮箱地址" prop="email">
              <el-input
                v-model="form.email"
                disabled
                size="large"
                :prefix-icon="Message"
              >
                <template #append>
                  <el-button class="email-verified-btn">
                    <el-icon><CircleCheck /></el-icon>
                    已认证
                  </el-button>
                </template>
              </el-input>
            </el-form-item>
          </div>

          <!-- 区域三：密码与安全折叠区 -->
          <div class="form-section-block">
            <el-collapse v-model="activeCollapse" class="modern-collapse">
              <el-collapse-item name="change-password">
                <template #title>
                  <div class="collapse-header-slot">
                    <el-icon class="lock-icon"><Lock /></el-icon>
                    <span class="title-txt">修改账号登录密码</span>
                    <span class="sub-txt">（若无需变更密码可保持折叠）</span>
                  </div>
                </template>

                <div class="password-fields-container">
                  <el-form-item
                    :label="$t('settings.password') || '当前旧密码'"
                    prop="password"
                  >
                    <el-input
                      type="password"
                      v-model="form.password"
                      show-password
                      placeholder="请输入当前生效的旧密码"
                      size="large"
                    />
                  </el-form-item>

                  <el-form-item
                    :label="$t('settings.newPassword') || '设置新密码'"
                    prop="newPassword"
                  >
                    <el-input
                      type="password"
                      v-model="form.newPassword"
                      show-password
                      placeholder="6 到 16 位字符，推荐包含字母与数字"
                      size="large"
                    />
                  </el-form-item>

                  <el-form-item
                    :label="$t('settings.confirmPassword') || '再次确认新密码'"
                    prop="confirmNewPassword"
                  >
                    <el-input
                      type="password"
                      v-model="form.confirmNewPassword"
                      show-password
                      placeholder="请再次输入新密码以确认"
                      size="large"
                    />
                  </el-form-item>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>

          <!-- 底部保存操作按钮 -->
          <div class="form-action-bar">
            <el-button
              class="cancel-btn"
              size="large"
              @click="$router.back(-1)"
            >
              {{ $t("settings.back") || "取消并返回" }}
            </el-button>
            <el-button
              type="primary"
              class="save-btn"
              size="large"
              :loading="saving"
              @click="handleSave"
            >
              <el-icon><Check /></el-icon>
              {{ $t("settings.save") || "保存设置" }}
            </el-button>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from "vue";
import { ElMessage } from "element-plus";
import {
  Plus,
  Delete,
  PictureFilled,
  UserFilled,
  User,
  Key,
  Message,
  CircleCheck,
  Lock,
  Check,
} from "@element-plus/icons-vue";

export default defineComponent({
  name: "SettingsForm",
  components: {
    Plus,
    Delete,
    PictureFilled,
    UserFilled,
    Key,
    CircleCheck,
    Lock,
    Check,
  },
  data() {
    return {
      User: markRaw(User),
      Message: markRaw(Message),
      saving: false,
      form: {
        name: "",
        password: "",
        newPassword: "",
        confirmNewPassword: "",
        identity: "",
        email: "",
      },
      imageUrl: "",
      fileList: [] as any[],
      activeCollapse: [] as string[],
      options: [
        { value: "学生", label: "学生" },
        { value: "老师", label: "老师" },
        { value: "机构", label: "机构" },
        { value: "体验者", label: "体验者" },
      ],
    };
  },
  computed: {
    allowUpload(): boolean {
      return Boolean(this.imageUrl);
    },
    UpdateApi(): string {
      return process.env.VUE_APP_Update_API || "";
    },
    isPasswordOpen(): boolean {
      return Array.isArray(this.activeCollapse)
        ? this.activeCollapse.includes("change-password")
        : this.activeCollapse === "change-password";
    },
    rules(): any {
      return {
        name: [
          { required: true, message: "创作者昵称不能为空", trigger: "blur" },
          {
            min: 3,
            max: 15,
            message: "昵称长度需在 3 到 15 个字符之间",
            trigger: "blur",
          },
          {
            validator: (rule: any, value: string, callback: any) => {
              const pattern = /^[a-zA-Z0-9\u4e00-\u9fa5]+$/;
              if (!pattern.test(value)) {
                callback(new Error("只能包含中文、大小写字母与数字"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        password: [
          {
            validator: (rule: any, value: string, callback: any) => {
              if (this.isPasswordOpen && !value) {
                callback(new Error("请输入当前旧密码"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        newPassword: [
          {
            validator: (rule: any, value: string, callback: any) => {
              if (this.isPasswordOpen) {
                if (!value) {
                  callback(new Error("请输入新密码"));
                } else if (value.length < 6 || value.length > 16) {
                  callback(new Error("新密码长度需在 6 到 16 位之间"));
                } else {
                  callback();
                }
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        confirmNewPassword: [
          {
            validator: (rule: any, value: string, callback: any) => {
              if (this.isPasswordOpen) {
                if (!value) {
                  callback(new Error("请再次确认新密码"));
                } else if (value !== this.form.newPassword) {
                  callback(new Error("两次输入的新密码不一致"));
                } else {
                  callback();
                }
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
      };
    },
  },
  mounted() {
    const user = this.$store.state.user || {};
    this.imageUrl = user.avatar || "";
    this.form.name = user.name || "";
    this.form.email = user.email || "";
    this.form.identity = user.type || "";
  },
  methods: {
    handleSave() {
      const formEl = this.$refs.formRef as any;
      if (!formEl) return;

      formEl.validate((valid: boolean) => {
        if (!valid) return;

        this.saving = true;
        const payload: any = {
          name: this.form.name,
          headPic: this.imageUrl,
          type: this.form.identity,
        };

        // 仅在密码展开且通过校验时加入密码参数
        if (this.isPasswordOpen && this.form.confirmNewPassword) {
          payload.password = this.form.confirmNewPassword;
        }

        this.$store
          .dispatch("user/UpdateUser", payload)
          .then(() => {
            ElMessage.success("个人档案设置已更新");
            this.$router.push("/");
          })
          .catch((err: any) => {
            console.error(err);
            ElMessage.error("保存失败，请稍后重试");
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    beforeAvatarUpload(file: File): boolean {
      const isAllowed =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/webp";
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isAllowed) {
        ElMessage.warning("头像仅支持 JPG/PNG/WEBP 格式");
        return false;
      }
      if (!isLt2M) {
        ElMessage.warning("头像大小不能超过 2MB");
        return false;
      }
      return true;
    },
    handleAvatarSuccess(response: any) {
      if (response?.data?.returnFileUrl) {
        this.imageUrl = response.data.returnFileUrl;
      }
    },
    clearImage() {
      this.imageUrl = "";
      this.fileList = [];
    },
  },
});
</script>

<style scoped lang="less">
@primary-color: #5d5fef;
@primary-hover: #4b4de0;
@primary-light: #f0f2ff;
@text-main: #0f172a;
@text-sub: #475569;
@text-muted: #94a3b8;
@border-color: #e2e8f0;
@card-border: #f1f5f9;
@danger-color: #ef4444;

.settings-page-wrapper {
  width: 100%;
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 40px 20px 80px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  // 环境光
  .ambient-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    pointer-events: none;
    z-index: 0;

    &.glow-1 {
      width: 450px;
      height: 450px;
      background: rgba(93, 95, 239, 0.08);
      top: -60px;
      right: -60px;
    }

    &.glow-2 {
      width: 400px;
      height: 400px;
      background: rgba(14, 165, 233, 0.07);
      bottom: -40px;
      left: -60px;
    }
  }
}

.settings-card-container {
  max-width: 840px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

// 顶部头部卡片
.settings-header-card {
  background: #ffffff;
  border: 1px solid @card-border;
  border-radius: 16px;
  padding: 20px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.03);
  margin-bottom: 24px;

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;

    .header-icon-box {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: @primary-light;
      color: @primary-color;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 22px;
      flex-shrink: 0;
    }

    .header-text {
      .title {
        margin: 0 0 4px 0;
        font-size: 18px;
        font-weight: 700;
        color: @text-main;
      }
      .sub-title {
        margin: 0;
        font-size: 12.5px;
        color: @text-muted;
      }
    }
  }

  .header-right {
    .account-type-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #f1f5f9;
      color: @text-sub;
      font-size: 12px;
      font-weight: 600;
      padding: 4px 12px;
      border-radius: 20px;
      border: 1px solid @border-color;
    }
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
}

// 主表单面板
.settings-form-panel {
  background: #ffffff;
  border: 1px solid @card-border;
  border-radius: 18px;
  padding: 36px 40px;
  box-shadow: 0 4px 24px -2px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;

  @media (max-width: 640px) {
    padding: 24px 20px;
  }

  .form-section-block {
    margin-bottom: 30px;

    .section-title-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding-bottom: 8px;
      border-bottom: 1px solid @border-color;

      .indicator-bar {
        width: 4px;
        height: 16px;
        background: @primary-color;
        border-radius: 2px;
      }

      .section-name {
        font-size: 14.5px;
        font-weight: 700;
        color: @text-main;
      }
    }

    :deep(.el-form-item) {
      margin-bottom: 20px;

      .el-form-item__label {
        font-size: 13px;
        font-weight: 600;
        color: @text-sub;
        padding-bottom: 6px;
      }
    }

    :deep(.el-input__wrapper) {
      border-radius: 8px;
      box-shadow: 0 0 0 1px @border-color inset;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 1px #cbd5e1 inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px @primary-color inset !important;
      }

      &.is-disabled {
        background-color: #f8fafc;
        box-shadow: 0 0 0 1px @border-color inset;

        .el-input__inner {
          color: @text-muted;
          -webkit-text-fill-color: @text-muted;
        }
      }
    }
  }

  // 资料与头像行
  .profile-avatar-row {
    display: flex;
    align-items: center;
    gap: 36px;

    @media (max-width: 640px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
    }

    .avatar-uploader-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;

      .avatar-uploader {
        cursor: pointer;
        display: block;
      }

      .uploaded-avatar-wrapper {
        position: relative;
        width: 104px;
        height: 104px;
        border-radius: 50%;
        overflow: hidden;
        border: 2px solid @border-color;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);

        .avatar-img {
          width: 100%;
          height: 100%;
          display: block;
        }

        .avatar-fallback {
          width: 100%;
          height: 100%;
          background: #f1f5f9;
          color: @text-muted;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
        }

        .avatar-hover-mask {
          position: absolute;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(2px);
          display: flex;
          justify-content: center;
          align-items: center;
          opacity: 0;
          transition: opacity 0.2s ease;

          .del-icon-btn {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            background: #ffffff;
            color: @danger-color;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 15px;
            box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
            transition: transform 0.2s;

            &:hover {
              transform: scale(1.15);
            }
          }
        }

        &:hover .avatar-hover-mask {
          opacity: 1;
        }
      }

      .avatar-placeholder {
        width: 104px;
        height: 104px;
        border-radius: 50%;
        border: 1.5px dashed #cbd5e1;
        background: #f8fafc;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 4px;
        transition: all 0.2s;

        .upload-icon {
          font-size: 20px;
          color: @text-sub;
        }

        .upload-txt {
          font-size: 11px;
          font-weight: 600;
          color: @text-muted;
        }

        &:hover {
          border-color: @primary-color;
          background: @primary-light;

          .upload-icon,
          .upload-txt {
            color: @primary-color;
          }
        }
      }

      .avatar-tip {
        font-size: 11px;
        color: @text-muted;
      }
    }

    .profile-fields {
      flex: 1;
      width: 100%;
    }
  }

  // 邮箱认证后置标签
  .email-verified-btn {
    border: none;
    background: transparent;
    color: #10b981;
    font-weight: 600;
    cursor: default;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      background: transparent;
      color: #10b981;
    }
  }

  // 折叠密码修改面板
  .modern-collapse {
    border: 1px solid @border-color;
    border-radius: 12px;
    overflow: hidden;

    :deep(.el-collapse-item__header) {
      padding: 0 18px;
      height: 52px;
      line-height: 52px;
      font-size: 14px;
      background: #f8fafc;
      border-bottom: 1px solid transparent;
      transition: all 0.2s;

      &.is-active {
        border-bottom-color: @border-color;
        background: #ffffff;
      }
    }

    .collapse-header-slot {
      display: flex;
      align-items: center;
      gap: 8px;

      .lock-icon {
        font-size: 15px;
        color: @primary-color;
      }

      .title-txt {
        font-weight: 600;
        color: @text-main;
      }

      .sub-txt {
        font-size: 12px;
        color: @text-muted;
      }
    }

    .password-fields-container {
      padding: 20px 18px 4px;
      background: #ffffff;
    }
  }

  // 底部提交按钮
  .form-action-bar {
    margin-top: 36px;
    padding-top: 24px;
    border-top: 1px solid @border-color;
    display: flex;
    justify-content: flex-end;
    gap: 14px;

    .cancel-btn {
      height: 42px;
      padding: 0 22px;
      border-radius: 8px;
      border: 1px solid @border-color;
      color: @text-sub;
      font-weight: 500;

      &:hover {
        background: #f1f5f9;
        color: @text-main;
      }
    }

    .save-btn {
      height: 42px;
      padding: 0 28px;
      border-radius: 8px;
      font-weight: 600;
      background-color: @primary-color;
      border-color: @primary-color;
      box-shadow: 0 4px 14px rgba(93, 95, 239, 0.25);
      transition: all 0.2s ease;

      .el-icon {
        margin-right: 6px;
      }

      &:hover {
        background-color: @primary-hover;
        border-color: @primary-hover;
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(93, 95, 239, 0.35);
      }
    }
  }
}
</style>
